import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { TypeDeleteConfirmationModal } from '../../components/ui/TypeDeleteConfirmationModal';
import { ApiError } from '../../lib/api';
import { useCurrentUser } from '../auth';
import { listCampaigns } from '../campaigns/api';
import type {
  AccountCampaign,
  AccountCampaignBulkAction,
  AccountCampaignBulkResult,
} from '../../types/accountCampaign';
import type { Campaign } from '../../types/campaign';
import {
  bulkAction,
  changeRolloutStatus,
  getRolloutPlanBacklink,
  getRolloutRows,
  hardDeleteRollout,
  listUsers,
  refreshFields,
  refreshFolders,
  updateRollout,
} from './api';
import { BulkActionBar } from './BulkActionBar';
import { BulkRefreshFromCampaignModal } from './BulkRefreshFromCampaignModal';
import { CreateFoldersModal } from './CreateFoldersModal';
import { resolveBulkPreviewTarget } from './bulkPreviewGate';
import { resolveBulkSendTarget } from './bulkSendGate';
import {
  BulkPreviewModal,
  BulkSendPreviewModal,
  SendResultToast,
  SendStatusPanel,
} from '../preview';
import type { SendResult } from '../preview';
import { BulkRejectionToast } from './BulkRejectionToast';
import { DEFAULT_FILTERS, FilterBar, type Filters } from './FilterBar';
import { MasterGrid } from './MasterGrid';
import { applySelectAll } from './selection';
import { UpdateLocationListModal } from './UpdateLocationListModal';
import { ColumnPresetMenu, initialColumnStateFromPrefs } from './ColumnPresetMenu';
import { ROLLOUT_MASTER_GRID_KEY } from '../../types/user';
import { GroupReorderModal } from './GroupReorderModal';
import { RolloutTabStrip } from './RolloutTabStrip';
import { computeGridCounts } from './rolloutGridCounts';
import { bulkActionLabel } from './bulkActionLabels';
import {
  COLUMNS,
  TOP_GROUPS,
  canonicalizeOrder,
  type ColumnKey,
  type TopGroupKey,
} from './columns';

function rolloutStatusBadge(status: string): string {
  switch (status) {
    case 'draft':
      return 'bg-slate-100 text-slate-700';
    case 'active':
      return 'bg-emerald-100 text-emerald-800';
    case 'archived':
      return 'bg-slate-200 text-slate-500';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

export function RolloutDetail() {
  const { id } = useParams<{ id: string }>();
  const currentUser = useCurrentUser();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [showDelete, setShowDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const rollout = useQuery({
    queryKey: ['rollout', id, 'rows'],
    queryFn: () => getRolloutRows(id!),
    enabled: !!id,
    refetchOnWindowFocus: true,
  });

  const campaigns = useQuery({
    queryKey: ['campaigns', 'brand', rollout.data?.rollout.brandAccountId],
    queryFn: () =>
      listCampaigns({ brandAccountId: rollout.data!.rollout.brandAccountId }),
    enabled: !!rollout.data?.rollout.brandAccountId,
  });

  const users = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => listUsers(),
  });

  // Slice C §5.2: when this Rollout was spawned from a Promotion Plan,
  // resolve the Plan for the "Part of: <Plan>" back-link. Orphan Rollouts
  // (legacy + brand-account flow) have no promotionPlanId → query disabled,
  // no back-link shown.
  const planBacklinkId = rollout.data?.rollout.promotionPlanId ?? null;
  const planBacklink = useQuery({
    queryKey: ['rollout-plan-backlink', planBacklinkId],
    queryFn: () => getRolloutPlanBacklink(planBacklinkId!),
    enabled: !!planBacklinkId,
  });

  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkResult, setBulkResult] = useState<{ label: string; result: AccountCampaignBulkResult } | null>(null);
  // Granular Refresh spec §2. Open with the gated bulk action's resolved
  // single Campaign + the row ids it applies to.
  const [bulkRefreshTarget, setBulkRefreshTarget] = useState<{
    campaign: Campaign;
    selectedRows: AccountCampaign[];
  } | null>(null);
  // Ad Preview Mockups Stage 1 §3.2. Bulk preview target — snapshotted
  // at click time so the modal table stays stable even if the row list
  // refreshes underneath. Stage 1 §13.13 dropped the single-Campaign
  // gate — mixed Campaigns are admitted; resolveBulkPreviewTarget
  // applies per-row eligibility (campaign rowType + campaignId +
  // not soft-deleted).
  const [bulkPreviewTarget, setBulkPreviewTarget] = useState<
    Array<{ rowId: string; locationName: string; brandName: string | null }>
    | null
  >(null);
  // Ad Preview Stage 3 §3.2. Bulk-send target -- eligible row
  // descriptors snapshotted at click time, plus the result toast.
  const [bulkSendTarget, setBulkSendTarget] = useState<
    Array<{
      rowId: string;
      locationAccountId: string;
      previewLinkId: string;
      locationName: string;
    }>
    | null
  >(null);
  const [sendResult, setSendResult] = useState<SendResult | null>(null);
  const [showLocationSync, setShowLocationSync] = useState(false);
  const [showCreateFolders, setShowCreateFolders] = useState(false);
  const [showReorderGroups, setShowReorderGroups] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);

  // Column ordering + visibility (spec §7.1 polish). Initialised from the
  // signed-in user's activePresetId on mount. The initial order is run
  // through canonicalizeOrder so any stored preset that was saved before the
  // "groups reorder only" rule gets snapped to factory-within-group on read.
  // This is a PURE render-time transform -- we do NOT write the canonicalised
  // shape back to Firestore; the stored preset stays as-saved.
  const initial = useMemo(
    () => {
      const seed = initialColumnStateFromPrefs(
        currentUser.gridPreferences?.[ROLLOUT_MASTER_GRID_KEY],
      );
      return { order: canonicalizeOrder(seed.order), visible: seed.visible };
    },
    // Only on mount -- we don't want an incoming /api/me refetch to clobber
    // the user's in-progress tweaks. Preset switching goes through onApply.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [columnOrder, setColumnOrder] = useState<ColumnKey[]>(initial.order);
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(initial.visible);

  const applyPreset = (order: ColumnKey[], visible: Set<ColumnKey>) => {
    // Same canonicalisation on preset-switch -- guarantees within-group order
    // is factory even if the preset was authored under older rules.
    setColumnOrder(canonicalizeOrder(order));
    setVisibleColumns(visible);
  };

  // Top groups that currently have zero visible columns -- shown as chips
  // above the grid so AMs can re-expand a fully-hidden group without hunting
  // for the cog menu (which collapses with its cell).
  const hiddenTopGroups: TopGroupKey[] = useMemo(() => {
    const out: TopGroupKey[] = [];
    for (const tg of TOP_GROUPS) {
      const keys = COLUMNS.filter((c) => c.topGroup === tg.key).map((c) => c.key);
      if (keys.every((k) => !visibleColumns.has(k))) out.push(tg.key);
    }
    return out;
  }, [visibleColumns]);

  const restoreGroup = (tg: TopGroupKey) => {
    const keys = COLUMNS.filter((c) => c.topGroup === tg).map((c) => c.key);
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      for (const k of keys) next.add(k);
      return next;
    });
  };

  const bulkMut = useMutation({
    mutationFn: async (action: AccountCampaignBulkAction) => {
      if (!id) throw new Error('Missing rollout id');
      return bulkAction(id, action);
    },
  });

  const statusMut = useMutation({
    mutationFn: async (nextStatus: 'draft' | 'active' | 'archived') => {
      if (!id) throw new Error('Missing rollout id');
      setStatusBusy(true);
      return changeRolloutStatus(id, nextStatus);
    },
    onSettled: () => setStatusBusy(false),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rollout', id] }),
  });

  const deleteMut = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Missing rollout id');
      return hardDeleteRollout(id);
    },
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: ['rollouts'] });
      qc.removeQueries({ queryKey: ['rollout', id] });
      // The Rollout is gone; land on the now-unlocked parent Plan if it had
      // one, otherwise the Rollouts list.
      if (res.promotionPlanId) {
        await qc.invalidateQueries({
          queryKey: ['plan', res.promotionPlanId],
        });
        navigate(`/plans/${res.promotionPlanId}`);
      } else {
        navigate('/rollouts');
      }
    },
    onError: (err) =>
      setDeleteError(err instanceof ApiError ? err.message : String(err)),
  });

  // Pre-P2b-3 polish round 5 §2.3: rollout-level monthYear edit. The
  // small inline-edit affordance is the only currently-editable
  // rollout-level field; see docs/ux-debt.md for the broader extension.
  const monthYearMut = useMutation({
    mutationFn: async (next: string) => {
      if (!id) throw new Error('Missing rollout id');
      return updateRollout(id, { monthYearLabel: next });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rollout', id] }),
  });

  // OrganicAssets spec §4.2. Folder-presence refresh. The effect below fires
  // an `auto` refresh on grid load (server honours the 5-min per-row freshness
  // window, so re-mounts are cheap); rows are refetched only when presence
  // actually changed. The "Check folders" button force-refreshes (`manual`).
  const folderRefreshMut = useMutation({
    mutationFn: (mode: 'auto' | 'manual') => {
      if (!id) throw new Error('Missing rollout id');
      return refreshFolders(id, mode);
    },
    onSuccess: (res) => {
      if (res.rowsUpdated > 0) {
        qc.invalidateQueries({ queryKey: ['rollout', id, 'rows'] });
      }
    },
  });
  // Fire once per rollout id. Deliberately keyed only on `id` so a rows
  // refetch (e.g. window-focus) doesn't re-trigger the Drive sweep.
  useEffect(() => {
    if (id) folderRefreshMut.mutate('auto');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const bulkLabel = bulkActionLabel;

  const dispatchBulk = async (action: AccountCampaignBulkAction) => {
    try {
      const result = await bulkMut.mutateAsync(action);
      setBulkResult({ label: bulkLabel(action), result });
      await qc.invalidateQueries({ queryKey: ['rollout', id, 'rows'] });
      // Keep selection — user may want to try a different action on the same set.
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      setBulkResult({
        label: bulkLabel(action),
        result: {
          succeeded: 0,
          rejected: [{ accountCampaignId: '(none)', locationName: '(all rows)', reasons: [msg] }],
        },
      });
    }
  };

  // Visible campaigns = the union of all Campaigns attached to rows PLUS
  // every active Campaign for this brand (so assignment dropdowns work).
  const allCampaigns = campaigns.data ?? [];
  const campaignsUsedIds = useMemo(() => {
    return new Set((rollout.data?.rows ?? []).map((r) => r.campaignId).filter((x): x is string => !!x));
  }, [rollout.data]);
  const campaignsUsed = useMemo(
    () => allCampaigns.filter((c) => campaignsUsedIds.has(c.id)),
    [allCampaigns, campaignsUsedIds],
  );

  // Derive group filter inputs: distinct group names + whether any row is
  // Independent (parent = brand). Mirrors MasterGrid.resolveGroupTag so the
  // filter matches the Location column's tag exactly.
  const { groupsInRollout, hasIndependentLocations } = useMemo(() => {
    const accs = rollout.data?.accounts ?? [];
    const rowsData = rollout.data?.rows ?? [];
    const byId = new Map(accs.map((a) => [a.id, a]));
    const groupSet = new Set<string>();
    let anyIndependent = false;
    for (const row of rowsData) {
      let cursor = byId.get(row.accountId)?.parentAccountId ?? null;
      let groupName: string | null = null;
      while (cursor) {
        const anc = byId.get(cursor);
        if (!anc) break;
        if (anc.accountType === 'brand') break;
        if (anc.accountType !== 'location') {
          groupName = anc.name;
          break;
        }
        cursor = anc.parentAccountId;
      }
      if (groupName === null) anyIndependent = true;
      else groupSet.add(groupName);
    }
    return {
      groupsInRollout: [...groupSet].sort((a, b) => a.localeCompare(b)),
      hasIndependentLocations: anyIndependent,
    };
  }, [rollout.data]);

  if (rollout.isLoading || campaigns.isLoading || users.isLoading) return <CenteredSpinner />;
  if (rollout.error || !rollout.data) {
    return (
      <div className="text-red-600 text-sm">
        Failed to load rollout: {(rollout.error as Error | undefined)?.message ?? 'not found'}
      </div>
    );
  }

  const { rollout: r, rows, accounts, locationProfiles, brandFranchiseProfile } = rollout.data;
  const archived = r.status === 'archived';
  const brand = accounts.find((a) => a.id === r.brandAccountId);

  // OrganicAssets spec §3.1 / §6.3: rollout-level "Create Meta Asset Folders"
  // eligibility. Needs the brand's Drive parent folder configured AND at least
  // one row with an assigned Campaign (rowType 'campaign'). Opt-out + unassigned
  // rows are "skipped". Pre-counts feed the confirmation modal; the server's
  // result carries the authoritative tallies.
  const brandDriveConfigured = !!brandFranchiseProfile?.driveRolloutParentFolderId;
  const eligibleFolderRows = rows.filter((row) => row.rowType === 'campaign' && !!row.campaignId);
  const folderCampaignCount = new Set(eligibleFolderRows.map((row) => row.campaignId)).size;
  const folderRowsSkipped = rows.length - eligibleFolderRows.length;
  const createFoldersDisabledReason = archived
    ? 'Rollout is archived (read-only).'
    : !brandDriveConfigured
      ? "Configure the brand's Drive parent folder first (Brand settings)."
      : eligibleFolderRows.length === 0
        ? 'Assign a Campaign to at least one row first.'
        : null;

  // P2b-3 §1.2: gate-only counts for the tab strip. Computed from the full
  // master rows + Campaign cache so the badge stays stable as filter-bar
  // state changes. Recomputed on every render — the row count is bounded
  // and the predicate is constant-time per row.
  const gridCounts = (() => {
    const accountsById = new Map(accounts.map((a) => [a.id, a]));
    const campaignsById = new Map(allCampaigns.map((c) => [c.id, c]));
    const locationProfilesById = new Map(
      locationProfiles.map((p) => [p.accountId, p]),
    );
    return computeGridCounts({
      rows,
      accountsById,
      campaignsById,
      locationProfilesById,
      rolloutArchived: archived,
    });
  })();

  const toggleSelect = (id: string, next: boolean) => {
    setSelectedIds((prev) => {
      const copy = new Set(prev);
      if (next) copy.add(id);
      else copy.delete(id);
      return copy;
    });
  };
  // Item 2: Select All operates on the grid's *filtered* row ids (passed
  // up from MasterGrid.visibleRows), not every row in the rollout. The
  // passed ids are already the non-deleted, filter-matching set.
  const toggleAll = (next: boolean, visibleIds: string[]) => {
    setSelectedIds((prev) => applySelectAll(prev, visibleIds, next));
  };

  return (
    <div className="space-y-4">
      <RolloutTabStrip
        rolloutId={r.id}
        active="master"
        metaCount={gridCounts.meta}
        googleCount={gridCounts.google}
      />
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs text-slate-500">
            <Link to="/rollouts" className="hover:underline">Rollouts</Link>
            <span className="mx-1 text-slate-300">/</span>
            {brand ? (
              <Link to={`/accounts/${brand.id}`} className="hover:underline">
                {brand.name}
              </Link>
            ) : null}
          </p>
          <h1 className="text-2xl font-semibold text-slate-900">{r.name}</h1>
          {r.promotionPlanId && (
            <p className="mt-1 text-sm text-slate-500">
              Part of:{' '}
              <Link
                to={`/plans/${r.promotionPlanId}`}
                className="font-medium text-slate-700 hover:underline"
              >
                {planBacklink.data?.name ?? 'Promotion Plan'} ↗
              </Link>
            </p>
          )}
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-xs px-2 py-0.5 rounded-full ${rolloutStatusBadge(r.status)}`}>
              {r.status}
            </span>
            <span className="text-sm text-slate-500">
              {rows.length} {rows.length === 1 ? 'row' : 'rows'}
            </span>
            {/* Pre-P2b-3 polish round 5 §2.3: inline edit for the
                rollout's monthYear label. Drives {monthYear}
                substitution in every Meta name + Google ad-label
                template across this rollout. Only currently-editable
                rollout-level field; see docs/ux-debt.md for the
                broader pattern. */}
            <MonthYearLabelEdit
              value={r.monthYearLabel ?? ''}
              busy={monthYearMut.isPending}
              disabled={archived}
              onCommit={(next) => {
                if (!next.trim() || next === r.monthYearLabel) return;
                monthYearMut.mutate(next.trim());
              }}
            />
          </div>
          {r.notes && (
            <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap">{r.notes}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {folderRefreshMut.isPending && (
            <span className="text-xs text-slate-500 inline-flex items-center gap-1">
              <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
              Checking folders…
            </span>
          )}
          <Button
            variant="outline"
            onClick={() => folderRefreshMut.mutate('manual')}
            disabled={folderRefreshMut.isPending}
            title="Re-check every row's Local + Organic Drive folders for files"
          >
            Check folders
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowCreateFolders(true)}
            disabled={createFoldersDisabledReason !== null}
            title={
              createFoldersDisabledReason ??
              'Create Local + Organic Drive folders for every row with a Campaign'
            }
          >
            Create Meta Asset Folders
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowLocationSync(true)}
            disabled={archived}
          >
            Update location list
          </Button>
          {r.status !== 'archived' && (
            <Button
              variant="outline"
              onClick={() => {
                if (
                  !window.confirm(
                    'Archive this rollout? Rows freeze and all mutations are rejected. This is reversible via the status switcher.',
                  )
                )
                  return;
                statusMut.mutate('archived');
              }}
              disabled={statusBusy}
            >
              Archive
            </Button>
          )}
          {r.status === 'archived' && (
            <Button
              variant="outline"
              onClick={() => statusMut.mutate('active')}
              disabled={statusBusy}
            >
              Re-activate
            </Button>
          )}
          {r.status === 'draft' && (
            <Button onClick={() => statusMut.mutate('active')} disabled={statusBusy}>
              Make active
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => {
              setDeleteError(null);
              setShowDelete(true);
            }}
            className="!border-red-300 !text-red-600 hover:!bg-red-50"
          >
            Delete Rollout
          </Button>
        </div>
      </div>

      {showDelete && (
        <TypeDeleteConfirmationModal
          entityType="Rollout"
          entityName={r.name}
          impactMessage={`This permanently deletes ${rows.length} AccountCampaign row${
            rows.length === 1 ? '' : 's'
          }.${
            r.promotionPlanId
              ? ' Its parent Plan is unlocked and unlinked (not deleted).'
              : ''
          }`}
          busy={deleteMut.isPending}
          onCancel={() => setShowDelete(false)}
          onConfirm={() => deleteMut.mutate()}
        />
      )}
      {deleteError && (
        <p className="text-sm text-red-600">{deleteError}</p>
      )}

      <div className="flex items-start justify-between gap-2">
        <FilterBar
          filters={filters}
          onChange={setFilters}
          users={users.data ?? []}
          campaignsInRollout={campaignsUsed}
          groupsInRollout={groupsInRollout}
          hasIndependentLocations={hasIndependentLocations}
          currentUser={currentUser}
        />
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowReorderGroups(true)}
            className="rounded border border-slate-300 px-2 py-1.5 text-sm bg-white hover:bg-slate-50 inline-flex items-center gap-1"
            title="Reorder column groups"
            aria-label="Reorder column groups"
          >
            <span aria-hidden>⇅</span>
            <span className="hidden sm:inline">Reorder groups</span>
          </button>
          <ColumnPresetMenu
            currentUser={currentUser}
            columnOrder={columnOrder}
            visibleColumns={visibleColumns}
            onApply={applyPreset}
          />
        </div>
      </div>

      <BulkActionBar
        selectedIds={[...selectedIds]}
        onClear={() => setSelectedIds(new Set())}
        onDispatch={dispatchBulk}
        users={users.data ?? []}
        campaigns={allCampaigns}
        rows={rows}
        onRefreshFromCampaign={(c) =>
          setBulkRefreshTarget({
            campaign: c,
            selectedRows: rows.filter((r) => selectedIds.has(r.id)),
          })
        }
        onGeneratePreviews={() => {
          // Project the selection down to the descriptors the bulk modal
          // needs (location name + brand name + row id). Stage 1 §13.13
          // dropped the single-Campaign gate — we filter to ELIGIBLE
          // rows (campaign rowType + campaignId + not soft-deleted)
          // here so the modal table matches what the server will
          // actually preview-link. Ineligible rows in the selection
          // are silently dropped; the BulkActionBar's button is
          // already disabled when no rows are eligible.
          const gate = resolveBulkPreviewTarget([...selectedIds], rows);
          const accById = new Map(accounts.map((a) => [a.id, a]));
          const brandAcc = accById.get(r.brandAccountId) ?? null;
          const eligibleSet = new Set(gate.eligibleIds);
          const descriptors = rows
            .filter((row) => eligibleSet.has(row.id))
            .map((row) => ({
              rowId: row.id,
              locationName: accById.get(row.accountId)?.name ?? row.accountId,
              brandName: brandAcc?.name ?? null,
            }));
          if (descriptors.length > 0) setBulkPreviewTarget(descriptors);
        }}
        onSendPreviews={() => {
          // Project the selection to the eligible rows (campaign +
          // assigned Campaign + not deleted + has a preview link to
          // send) and snapshot the descriptors the bulk-send modal
          // needs. The server re-checks link validity per row.
          const gate = resolveBulkSendTarget([...selectedIds], rows);
          const accById = new Map(accounts.map((a) => [a.id, a]));
          const eligibleSet = new Set(gate.eligibleIds);
          const descriptors = rows
            .filter((row) => eligibleSet.has(row.id) && !!row.latestPreviewLinkId)
            .map((row) => ({
              rowId: row.id,
              locationAccountId: row.accountId,
              previewLinkId: row.latestPreviewLinkId as string,
              locationName: accById.get(row.accountId)?.name ?? row.accountId,
            }));
          if (descriptors.length > 0) setBulkSendTarget(descriptors);
        }}
        disabled={archived}
      />

      {bulkRefreshTarget && (
        <BulkRefreshFromCampaignModal
          campaign={bulkRefreshTarget.campaign}
          selectedRows={bulkRefreshTarget.selectedRows}
          onSubmit={async (fieldKeys) => {
            if (!id) throw new Error('Missing rollout id');
            await refreshFields(
              id,
              bulkRefreshTarget.selectedRows.map((r) => r.id),
              fieldKeys,
            );
            await qc.invalidateQueries({ queryKey: ['rollout', id, 'rows'] });
          }}
          onClose={() => setBulkRefreshTarget(null)}
        />
      )}

      {bulkPreviewTarget && id && (
        <BulkPreviewModal
          rolloutId={id}
          rows={bulkPreviewTarget}
          onClose={() => setBulkPreviewTarget(null)}
        />
      )}

      {/* Ad Preview Stage 3 §3.2: bulk-send modal + result toast. */}
      {bulkSendTarget && id && (
        <BulkSendPreviewModal
          rolloutId={id}
          rows={bulkSendTarget}
          onClose={() => setBulkSendTarget(null)}
          onSent={(result) => setSendResult(result)}
        />
      )}
      {sendResult && (
        <SendResultToast result={sendResult} onDismiss={() => setSendResult(null)} />
      )}

      {hiddenTopGroups.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600 border border-slate-200 bg-slate-50 rounded-md px-3 py-2">
          <span className="font-medium">Hidden groups:</span>
          {hiddenTopGroups.map((key) => {
            const tg = TOP_GROUPS.find((g) => g.key === key)!;
            return (
              <button
                key={key}
                type="button"
                onClick={() => restoreGroup(key)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white border border-slate-200 hover:border-slate-400 hover:bg-slate-100"
                title={`Re-show all columns in ${tg.label}`}
              >
                {tg.label}
                <span className="text-slate-400">▸</span>
              </button>
            );
          })}
        </div>
      )}

      <MasterGrid
        rollout={r}
        rows={rows}
        accounts={accounts}
        locationProfiles={locationProfiles}
        campaigns={allCampaigns}
        users={users.data ?? []}
        currentUser={currentUser}
        filters={filters}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleAll={toggleAll}
        columnOrder={columnOrder}
        visibleColumns={visibleColumns}
        onVisibleColumnsChange={setVisibleColumns}
        brandDriveConfigured={brandDriveConfigured}
      />

      {showReorderGroups && (
        <GroupReorderModal
          columnOrder={columnOrder}
          onClose={() => setShowReorderGroups(false)}
          onSave={(next) => setColumnOrder(next)}
        />
      )}

      {showLocationSync && (
        <UpdateLocationListModal rolloutId={r.id} onClose={() => setShowLocationSync(false)} />
      )}

      {showCreateFolders && id && (
        <CreateFoldersModal
          rolloutId={id}
          rowsWithCampaign={eligibleFolderRows.length}
          campaignCount={folderCampaignCount}
          rowsSkippedNoCampaign={folderRowsSkipped}
          onClose={() => setShowCreateFolders(false)}
          onDone={() => qc.invalidateQueries({ queryKey: ['rollout', id, 'rows'] })}
        />
      )}

      {bulkResult && (
        <BulkRejectionToast
          actionLabel={bulkResult.label}
          result={bulkResult.result}
          onDismiss={() => setBulkResult(null)}
        />
      )}

      {/* Ad Preview Stage 3 §6.1: Preview send history. Collapsed by
          default so it doesn't push the grid down; the AM expands it
          to review batches + retry failures. */}
      {id && (
        <details className="mt-6 rounded-md border border-slate-200 bg-white">
          <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-slate-800">
            Preview send history
          </summary>
          <div className="border-t border-slate-100 p-4">
            <SendStatusPanel rolloutId={id} />
          </div>
        </details>
      )}
    </div>
  );
}

// Pre-P2b-3 polish round 5 §2.3: small inline edit for the rollout's
// monthYearLabel. Click-to-reveal-input pattern. Empty input or no-op
// commit (same value) is dropped silently — the parent's onCommit gate
// handles that — so blur with no edit just collapses back to view mode.
//
// Display when not editing: small muted label with a pencil icon.
// Display when editing: bordered text input + Save / Cancel buttons.
function MonthYearLabelEdit({
  value,
  busy,
  disabled,
  onCommit,
}: {
  value: string;
  busy: boolean;
  disabled: boolean;
  onCommit: (next: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (!editing) {
    return (
      <button
        type="button"
        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
        disabled={disabled}
        onClick={() => {
          setDraft(value);
          setEditing(true);
        }}
        title={
          disabled
            ? 'Archived — month-year label is read-only'
            : 'Edit month-year label (drives {monthYear} substitution)'
        }
      >
        <span className="font-medium">{value || '—'}</span>
        <span className="text-slate-400">✎</span>
      </button>
    );
  }

  const save = () => {
    onCommit(draft);
    setEditing(false);
  };
  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  return (
    <span className="inline-flex items-center gap-1">
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        autoFocus
        disabled={busy}
        className="text-xs px-2 py-0.5 rounded border border-slate-300 w-24"
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') cancel();
        }}
      />
      <button
        type="button"
        className="text-xs px-2 py-0.5 rounded bg-slate-900 text-white disabled:opacity-50"
        disabled={busy}
        onClick={save}
      >
        Save
      </button>
      <button
        type="button"
        className="text-xs px-2 py-0.5 rounded text-slate-500 hover:text-slate-900"
        disabled={busy}
        onClick={cancel}
      >
        Cancel
      </button>
    </span>
  );
}
